import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Toaster } from "sonner";
import { DashboardLayout } from "./components/Layout/DashboardLayout";
import { ProtectedRoute } from "./components/Common/ProtectedRoute";
import { ConfirmHost } from "./components/Common/DeleteConfirm";
import Login from "./features/auth/Login";
import Dashboard from "./features/dashboard/Dashboard";
import BrandList from "./features/brands/BrandList";
import BrandForm from "./features/brands/BrandForm";
import BlogList from "./features/blog/BlogList";
import BlogForm from "./features/blog/BlogForm";
import ContactList from "./features/contacts/ContactList";
import DealerList from "./features/dealers/DealerList";
import NewsletterList from "./features/newsletter/NewsletterList";
import JobList from "./features/jobs/JobList";
import JobForm from "./features/jobs/JobForm";
import SocialList from "./features/social/SocialList";
import SocialForm from "./features/social/SocialForm";
import PagesSeo from "./features/pages-seo/PagesSeo";
import MediaBrowser from "./features/media/MediaBrowser";
import ContentEditor from "./features/content/ContentEditor";
import SettingsPage from "./features/settings/SettingsPage";
import FooterPage from "./features/footer/FooterPage";
import JobApplicationsList from "./features/job-applications/JobApplicationsList";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-screen">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="brands" element={<BrandList />} />
              <Route path="brands/new" element={<BrandForm />} />
              <Route path="brands/:id/edit" element={<BrandForm />} />
              <Route path="blog" element={<BlogList />} />
              <Route path="blog/new" element={<BlogForm />} />
              <Route path="blog/:id/edit" element={<BlogForm />} />
              <Route path="contacts" element={<ContactList />} />
              <Route path="dealers" element={<DealerList />} />
              <Route path="newsletter" element={<NewsletterList />} />
              <Route path="jobs" element={<JobList />} />
              <Route path="jobs/new" element={<JobForm />} />
              <Route path="jobs/:id/edit" element={<JobForm />} />
              <Route path="job-applications" element={<JobApplicationsList />} />
              <Route path="social" element={<SocialList />} />
              <Route path="social/new" element={<SocialForm />} />
              <Route path="social/:id/edit" element={<SocialForm />} />
              <Route path="pages" element={<PagesSeo />} />
              <Route path="media" element={<MediaBrowser />} />
              <Route path="page-content" element={<ContentEditor />} />
              <Route path="footer" element={<FooterPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </Suspense>
        {/* Global confirm dialog host – always mounted, renders nothing when closed */}
        <ConfirmHost />
        {/* Global toast provider */}
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
